// MaturaMod5SquaresOpenTask.js
// Zadanie otwarte (dowĂłd): jeĹ›li a â‰ˇ r_a (mod m) i b â‰ˇ r_b (mod m),
// to a^2 â’ b^2 jest podzielna przez m (dla wybranych m i par reszt).
// Wersja: bez podpowiadania odpowiedzi w placeholderach + 2 inputy w kroku 1
// + elastyczny input: uczeĹ„ moĹĽe wpisaÄ‡ "3k+1" albo "a=3k+1" (analogicznie dla pozostaĹ‚ych krokĂłw).

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import EquationInput from '../../../../components/inputs/EquationInput';

const normalize = (txt) =>
  String(txt || '')
    .replace(/\s/g, '')
    .toLowerCase()
    .replace(/Â·/g, '*')
    .replace(/â’/g, '-');

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// ===========================
// Helpers: stripping prefixes
// ===========================

function stripLeadingEq(raw, allowedLeftPrefixes) {
  const s = String(raw ?? '').trim();
  if (!s) return '';

  const noSpaces = s.replace(/\s/g, '');
  const eqIdx = noSpaces.indexOf('=');
  if (eqIdx === -1) return s;

  const left = noSpaces.slice(0, eqIdx).toLowerCase();
  const right = noSpaces.slice(eqIdx + 1);

  const ok = allowedLeftPrefixes.some((p) => left === p);
  return ok ? right : s;
}

function stripForA(raw) {
  return stripLeadingEq(raw, ['a']);
}

function stripForB(raw) {
  return stripLeadingEq(raw, ['b']);
}

function stripForDiff(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return '';

  const compact = s.replace(/\s/g, '');
  const eqIdx = compact.indexOf('=');
  if (eqIdx === -1) return s;

  const left = compact.slice(0, eqIdx).toLowerCase();

  const candidates = [
    'a^2-b^2',
    'aÂ˛-bÂ˛',
    'a^{2}-b^{2}',
    'a^2â’b^2',
    'aÂ˛â’bÂ˛',
    'a^{2}â’b^{2}',
  ];

  const ok = candidates.some((c) => left === c.toLowerCase());
  return ok ? compact.slice(eqIdx + 1) : s;
}

function detectFirstVarLetter(raw) {
  const s = String(raw ?? '');
  const m = s.match(/[a-z]/i);
  return m ? m[0].toLowerCase() : null;
}

// Parsuje postaÄ‡: m*var + c (rĂłĹĽne warianty)
// Akceptuje np. "3x+2", "3*x+2", "3(x)+2", "3x-1"
// Zwraca { var, constTerm } albo null
function parseLinearModForm(rhsRaw, modulus) {
  const s0 = String(rhsRaw ?? '').trim();
  if (!s0) return null;

  const s = normalize(s0);

  // UsuĹ„ nawiasy wokĂłĹ‚ samej zmiennej: 3(x)+2 -> 3x+2
  const s1 = s.replace(/\(([a-z])\)/g, '$1');

  const re = /^(\d+)\*?([a-z])([+\-]\d+)$/i;
  const m = s1.match(re);
  if (!m) return null;

  const coeff = parseInt(m[1], 10);
  const v = String(m[2]).toLowerCase();
  const c = parseInt(m[3], 10);

  if (!Number.isFinite(coeff) || !Number.isFinite(c)) return null;
  if (coeff !== modulus) return null;

  return { var: v, constTerm: c };
}

function modEq(constTerm, modulus, expectedRest) {
  const r = ((constTerm % modulus) + modulus) % modulus;
  return r === expectedRest;
}

// ===========================
// Latex rendering for previews
// ===========================

function toUserLatex(str) {
  let s = String(str ?? '');

  s = s.replace(/(?<!\\dfrac\{)(-?\d+)\/(\d+)/g, (_, n, d) => `\\dfrac{${n}}{${d}}`);
  s = s.replace(/\^(-?\d{2,})/g, '^{$1}');
  s = s.replace(/\*/g, '\\cdot ');
  s = s.replace(/Â·/g, '\\cdot ');

  return s;
}

// ============================================================================
// POLYNOMIAL PARSER (x,y) â€“ elastyczne sprawdzanie kroku 2 i 3
// PorĂłwnujemy wielomiany w postaci kanonicznej: wspĂłĹ‚czynniki dla
// x^2, y^2, xy, x, y, const. Wystarcza do tego zadania.
// ============================================================================

const POLY_KEYS = ['x2', 'y2', 'xy', 'x', 'y', 'c'];

function polyZero() {
  return { x2: 0, y2: 0, xy: 0, x: 0, y: 0, c: 0 };
}

function polyAdd(a, b) {
  const out = polyZero();
  for (const k of POLY_KEYS) out[k] = (a[k] || 0) + (b[k] || 0);
  return out;
}

function polySub(a, b) {
  const out = polyZero();
  for (const k of POLY_KEYS) out[k] = (a[k] || 0) - (b[k] || 0);
  return out;
}

function polyMul(a, b) {
  // MnoĹĽymy i obcinamy do stopnia 2 (w tym zadaniu i tak wyjdzie <=2).
  // JeĹ›li pojawi siÄ™ coĹ› wyĹĽszego, uznamy to za niepoprawny zapis.
  const out = polyZero();

  const termsA = polyToTerms(a);
  const termsB = polyToTerms(b);

  for (const ta of termsA) {
    for (const tb of termsB) {
      const coeff = ta.coeff * tb.coeff;
      const ex = ta.ex + tb.ex;
      const ey = ta.ey + tb.ey;
      if (ex + ey > 2) return null; // poza zakresem zadania
      const key = monoKey(ex, ey);
      if (!key) return null;
      out[key] += coeff;
    }
  }
  return out;
}

function polyPow2(p) {
  return polyMul(p, p);
}

function polyToTerms(p) {
  const terms = [];
  for (const k of POLY_KEYS) {
    const coeff = p[k] || 0;
    if (!coeff) continue;
    const { ex, ey } = keyToExp(k);
    terms.push({ coeff, ex, ey });
  }
  return terms;
}

function monoKey(ex, ey) {
  if (ex === 2 && ey === 0) return 'x2';
  if (ex === 0 && ey === 2) return 'y2';
  if (ex === 1 && ey === 1) return 'xy';
  if (ex === 1 && ey === 0) return 'x';
  if (ex === 0 && ey === 1) return 'y';
  if (ex === 0 && ey === 0) return 'c';
  return null;
}

function keyToExp(k) {
  switch (k) {
    case 'x2':
      return { ex: 2, ey: 0 };
    case 'y2':
      return { ex: 0, ey: 2 };
    case 'xy':
      return { ex: 1, ey: 1 };
    case 'x':
      return { ex: 1, ey: 0 };
    case 'y':
      return { ex: 0, ey: 1 };
    case 'c':
    default:
      return { ex: 0, ey: 0 };
  }
}

function polyEq(a, b) {
  for (const k of POLY_KEYS) {
    if ((a[k] || 0) !== (b[k] || 0)) return false;
  }
  return true;
}

function polyDivByInt(p, m) {
  const out = polyZero();
  for (const k of POLY_KEYS) {
    const v = p[k] || 0;
    if (v % m !== 0) return null;
    out[k] = v / m;
  }
  return out;
}

// --- Tokenizer / parser for simple algebra ---
function insertImplicitMultiplication(s) {
  const src = String(s || '');
  let out = '';
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    const prev = out[out.length - 1] || '';

    const isPrevAtom = /[0-9a-z)]/i.test(prev);
    const isNextAtomStart = ch === '(' || /[0-9a-z]/i.test(ch);

    if (i > 0 && isPrevAtom && isNextAtomStart) {
      const prevIsDigit = /[0-9]/.test(prev);
      const chIsDigit = /[0-9]/.test(ch);
      if (!(prevIsDigit && chIsDigit)) {
        const prevIsLetter = /[a-z]/i.test(prev);
        const chIsLetter = /[a-z]/i.test(ch);
        if (!(prevIsLetter && chIsLetter)) {
          if (ch !== '^') out += '*';
        }
      }
    }
    out += ch;
  }
  return out;
}

function tokenize(exprRaw) {
  const expr = normalize(exprRaw);
  const s = insertImplicitMultiplication(expr);

  const tokens = [];
  let i = 0;

  while (i < s.length) {
    const ch = s[i];

    if (ch === ' ') {
      i += 1;
      continue;
    }
    if ('+-*/^()'.includes(ch)) {
      tokens.push({ t: ch });
      i += 1;
      continue;
    }
    if (/[0-9]/.test(ch)) {
      let j = i;
      while (j < s.length && /[0-9]/.test(s[j])) j += 1;
      tokens.push({ t: 'num', v: parseInt(s.slice(i, j), 10) });
      i = j;
      continue;
    }
    if (/[a-z]/i.test(ch)) {
      const v = ch.toLowerCase();
      tokens.push({ t: 'var', v });
      i += 1;
      continue;
    }

    return null;
  }

  return tokens;
}

function parsePoly(exprRaw) {
  const tokens = tokenize(exprRaw);
  if (!tokens) return null;

  let pos = 0;
  const peek = () => tokens[pos] || null;
  const eat = (t) => {
    if (peek() && peek().t === t) {
      pos += 1;
      return true;
    }
    return false;
  };

  function parseExpression() {
    let left = parseTerm();
    if (!left) return null;

    while (true) {
      if (eat('+')) {
        const right = parseTerm();
        if (!right) return null;
        left = polyAdd(left, right);
        continue;
      }
      if (eat('-')) {
        const right = parseTerm();
        if (!right) return null;
        left = polySub(left, right);
        continue;
      }
      break;
    }
    return left;
  }

  function parseTerm() {
    let left = parseFactor();
    if (!left) return null;

    while (true) {
      if (eat('*')) {
        const right = parseFactor();
        if (!right) return null;
        const prod = polyMul(left, right);
        if (!prod) return null;
        left = prod;
        continue;
      }
      break;
    }
    return left;
  }

  function parseFactor() {
    if (eat('+')) return parseFactor();
    if (eat('-')) {
      const f = parseFactor();
      if (!f) return null;
      const neg = polyZero();
      for (const k of POLY_KEYS) neg[k] = -(f[k] || 0);
      return neg;
    }

    let base = parsePrimary();
    if (!base) return null;

    if (eat('^')) {
      const tok = peek();
      if (!tok) return null;
      if (tok.t === 'num' && tok.v === 2) {
        pos += 1;
        const sq = polyPow2(base);
        return sq;
      }
      return null;
    }

    return base;
  }

  function parsePrimary() {
    const tok = peek();
    if (!tok) return null;

    if (tok.t === 'num') {
      pos += 1;
      const p = polyZero();
      p.c = tok.v;
      return p;
    }

    if (tok.t === 'var') {
      pos += 1;
      const v = tok.v;
      if (v !== 'x' && v !== 'y') return null;
      const p = polyZero();
      p[v] = 1;
      return p;
    }

    if (eat('(')) {
      const inside = parseExpression();
      if (!inside) return null;
      if (!eat(')')) return null;
      return inside;
    }

    return null;
  }

  const res = parseExpression();
  if (!res) return null;
  if (pos !== tokens.length) return null;
  return res;
}

function buildExpectedPoly(modulus, aRest, bRest) {
  // (m x + r)^2 - (m y + s)^2
  const m = modulus;
  const r = aRest;
  const s = bRest;

  const left = polyZero();
  left.x2 = m * m;
  left.x = 2 * m * r;
  left.c = r * r;

  const right = polyZero();
  right.y2 = m * m;
  right.y = 2 * m * s;
  right.c = s * s;

  return polySub(left, right);
}

function hasExplicitFactorM(rawStep3, modulus) {
  const s = normalize(rawStep3);
  const mStr = String(modulus);
  if (s.startsWith(`${mStr}(`)) return true;
  if (s.includes(`${mStr}*(`)) return true;
  if (s.includes(`)*${mStr}`)) return true;
  return false;
}

/**
 * Krok 2 ma byÄ‡ "po podstawieniu i rozpisaniu kwadratĂłw",
 * a nie zostawione jako (mx+r)^2 - (my+s)^2.
 * Zatem zakazujemy wzorcĂłw typu ")^2" / ")^{2}".
 * (x^2 jest OK).
 */
function step2HasSquaredParentheses(rawStep2) {
  const s = normalize(rawStep2);
  if (!s) return false;
  // np. (5x+1)^2 albo (5y+4)^{2}
  if (/\)\^2/.test(s)) return true;
  if (/\)\^\{2\}/.test(s)) return true;
  return false;
}

// ============================================================================
// GENERATOR
// ============================================================================

function createJune2025OriginalTask() {
  return {
    type: 'MaturaMod5SquaresOpenTask',
    data: {
      modulus: 5,
      aRest: 1,
      bRest: 4,
      diffQuot: -3,
      maxPoints: 3,
    },
    source: {
      variant: 'original',
      exam: 'MATURA',
      session: '2025_CZE',
      taskNumber: 5,
    },
  };
}

export function generateTask(options = {}) {
  if (options.variant === 'original' && options.session === '2025_CZE') {
    return createJune2025OriginalTask();
  }

  const maxPoints = 3;

  const possibleModuli = [3, 4, 5, 6, 7];

  const residuePairsByMod = {
    3: [
      [1, 2],
      [2, 1],
    ],
    4: [
      [1, 3],
      [3, 1],
    ],
    5: [
      [1, 4],
      [4, 1],
      [2, 3],
      [3, 2],
    ],
    6: [
      [1, 5],
      [5, 1],
      [2, 4],
      [4, 2],
    ],
    7: [
      [1, 6],
      [6, 1],
      [2, 5],
      [5, 2],
      [3, 4],
      [4, 3],
    ],
  };

  const modulus = pick(possibleModuli);
  const [aRest, bRest] = pick(residuePairsByMod[modulus]);

  const r2 = aRest * aRest;
  const s2 = bRest * bRest;
  const diffSq = r2 - s2;
  const diffQuot = diffSq / modulus;

  return {
    type: 'MaturaMod5SquaresOpenTask',
    data: {
      modulus,
      aRest,
      bRest,
      diffQuot,
      maxPoints,
    },
  };
}

// ============================================================================
// TREĹšÄ† ZADANIA
// ============================================================================

export function renderTask({ data }) {
  const { modulus, aRest, bRest, maxPoints } = data;

  return (
    <div className="space-y-3" style={{ textAlign: 'left' }}>
      <p>
        <strong>Zadanie maturalne – otwarte (0–{maxPoints}).</strong>
      </p>
      <p>
        Wykaż, że dla każdej liczby całkowitej <InlineMath math={'a'} />, która
        przy dzieleniu przez <InlineMath math={String(modulus)} /> daje resztę{' '}
        <InlineMath math={String(aRest)} />, i dla każdej liczby całkowitej{' '}
        <InlineMath math={'b'} />, która przy dzieleniu przez{' '}
        <InlineMath math={String(modulus)} /> daje resztę{' '}
        <InlineMath math={String(bRest)} />, liczba{' '}
        <InlineMath math={'a^{2} - b^{2}'} /> jest podzielna przez{' '}
        <InlineMath math={String(modulus)} />.
      </p>
      <p>
        Skorzystaj z opisu reszt z dzielenia przez{' '}
        <InlineMath math={String(modulus)} /> oraz z przekształceń
        algebraicznych. Uzupełnij trzy kluczowe kroki dowodu.
      </p>
    </div>
  );
}

// ============================================================================
// INPUT â€“ trzy kroki dowodu
// ============================================================================

export function renderInput(task, userAnswer, onChange, disabled) {
  const { modulus } = task.data;
  const value = userAnswer || {};

  const handleStepChange = (stepKey) => (raw) => {
    onChange?.({ ...value, [stepKey]: raw });
  };

  const mStr = String(modulus);
  const step2Placeholder = 'np. 25x^2-25y^2+10x-40y-15';

  const staticPlaceholderA = 'np. 5x+1';
  const staticPlaceholderB = 'np. 5y+4';

  const varHintFor = (raw) => {
    const v = detectFirstVarLetter(raw);
    return v ? <InlineMath math={`${v} \\in \\mathbb{Z}`} /> : null;
  };

  return (
    <div className="space-y-4" style={{ marginTop: 12, textAlign: 'left' }}>
      {/* KROK 1 */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 1.</strong> Zapisz liczby <InlineMath math={'a'} /> oraz{' '}
          <InlineMath math={'b'} /> w postaci zależnej od{' '}
          <InlineMath math={mStr} />, używając liczb całkowitych.
        </div>

        <div style={{ marginBottom: 6, opacity: 0.9 }}>
          <InlineMath math={'a='} />
        </div>
        <EquationInput
          value={value.step1a || ''}
          onChange={handleStepChange('step1a')}
          disabled={disabled}
          placeholder={staticPlaceholderA}
          showPreview
        />
        {varHintFor(value.step1a) && (
          <div style={{ marginTop: 6, opacity: 0.85, fontSize: 12 }}>
            Założenie: {varHintFor(value.step1a)}
          </div>
        )}

        <div style={{ height: 12 }} />

        <div style={{ marginBottom: 6, opacity: 0.9 }}>
          <InlineMath math={'b='} />
        </div>
        <EquationInput
          value={value.step1b || ''}
          onChange={handleStepChange('step1b')}
          disabled={disabled}
          placeholder={staticPlaceholderB}
          showPreview
        />
        {varHintFor(value.step1b) && (
          <div style={{ marginTop: 6, opacity: 0.85, fontSize: 12 }}>
            Założenie: {varHintFor(value.step1b)}
          </div>
        )}
      </div>

      {/* KROK 2 */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 2.</strong> Zapisz różnicę{' '}
          <InlineMath math={'a^{2} - b^{2}'} /> w oparciu o postacie z kroku 1 i
          wykonaj podstawienie, rozwiń kwadraty i zapisz wynik po uproszczeniu
          (bez postaci <InlineMath math={'(...)^{2}'} />).
        </div>

        <div style={{ marginBottom: 6, opacity: 0.9 }}>
          <InlineMath math={'a^{2}-b^{2}='} />
        </div>
        <EquationInput
          value={value.step2 || ''}
          onChange={handleStepChange('step2')}
          disabled={disabled}
          placeholder={step2Placeholder}
          showPreview
        />
      </div>

      {/* KROK 3 */}
      <div>
        <div style={{ fontSize: 13, marginBottom: 6 }}>
          <strong>Krok 3.</strong> Wyłącz czynnik będący wielokrotnością{' '}
          <InlineMath math={mStr} /> i pokaż, że otrzymany wynik jest podzielny przez{' '}
          <InlineMath math={mStr} />.
        </div>

        <div style={{ marginBottom: 6, opacity: 0.9 }}>
          <InlineMath math={'a^{2}-b^{2}='} />
        </div>
        <EquationInput
          value={value.step3 || ''}
          onChange={handleStepChange('step3')}
          disabled={disabled}
          placeholder={`${mStr}(...)`}
          showPreview
        />
      </div>
    </div>
  );
}

// ============================================================================
// SPRAWDZANIE â€“ 0â€“3 pkt (po 1 za kaĹĽdy krok)
// - krok 1: poprawne reszty
// - jeĹ›li jedna zmienna w kroku 1: max 1 pkt + komunikat (CKE-like)
// - krok 2: musi byÄ‡ rĂłwnowaĹĽne expected ORAZ nie moĹĽe zostawiÄ‡ "(...)^2"
// - krok 3: rĂłwnowaĹĽne expected + jawny czynnik m + w nawiasie caĹ‚kowite
// ============================================================================

function mapUserVarsToXY(exprRaw, varA, varB) {
  let s = String(exprRaw ?? '');
  if (!s) return s;

  const a = String(varA || '').toLowerCase();
  const b = String(varB || '').toLowerCase();
  if (!a || !b || a === b) return s;

  // Zamieniaj literÄ™-zmiennÄ… tylko gdy nie stoi obok innej litery
  // DziÄ™ki temu dziaĹ‚a: 9k^2, 12k, (k+1), -m, itd.
  // A nie rusza np. "abc".
  const reA = new RegExp(`(?<![a-z])${a}(?![a-z])`, 'gi');
  const reB = new RegExp(`(?<![a-z])${b}(?![a-z])`, 'gi');

  s = s.replace(reA, '__VAR_A__');
  s = s.replace(reB, 'y');
  s = s.replace(/__VAR_A__/g, 'x');

  return s;
}

function findUnexpectedVars(rawExpr, allowedVars = []) {
  const s = normalize(rawExpr || '');
  if (!s) return [];
  const all = Array.from(new Set((s.match(/[a-z]/g) || []).map((v) => v.toLowerCase())));
  const allowed = new Set(
    ['a', 'b', ...allowedVars.map((v) => String(v || '').toLowerCase()).filter(Boolean)]
  );
  return all.filter((v) => !allowed.has(v));
}

export function checkAnswer({ data }, userAnswer) {
  const { modulus, aRest, bRest } = data;
  const maxScore = 3;

  const emptyResult = {
    isCorrect: false,
    score: 0,
    maxScore,
    parts: { step1: false, step2: false, step3: false },
  };

  if (!userAnswer || typeof userAnswer !== 'object') return emptyResult;

  // wejĹ›cia
  const step1aRhs = stripForA(userAnswer.step1a);
  const step1bRhs = stripForB(userAnswer.step1b);
  const step2RhsRaw = stripForDiff(userAnswer.step2);
  const step3RhsRaw = stripForDiff(userAnswer.step3);

  // --- KROK 1 ---
  const aParsed = parseLinearModForm(step1aRhs, modulus);
  const bParsed = parseLinearModForm(step1bRhs, modulus);

  const step1Ok =
    !!aParsed &&
    !!bParsed &&
    modEq(aParsed.constTerm, modulus, aRest) &&
    modEq(bParsed.constTerm, modulus, bRest);

  if (!step1Ok) return emptyResult;

  // jeĹ›li user uĹĽyĹ‚ tej samej zmiennej dla a i b -> max 1 pkt
  const distinctVarsOk =
    !!aParsed &&
    !!bParsed &&
    !!aParsed.var &&
    !!bParsed.var &&
    aParsed.var !== bParsed.var;

  if (!distinctVarsOk) {
    return {
      isCorrect: false,
      score: 1,
      maxScore,
      parts: { step1: true, step2: false, step3: false },
      messageType: 'warning',
      message:
        'Uwaga: Krok 1: trzeba wprowadzić dwie niezależne liczby całkowite (np. x i y). ' +
        'Zapis z jedną zmienną nie obejmuje wszystkich par (a,b), więc maksymalnie 1 punkt.',
    };
  }

  const expected = buildExpectedPoly(modulus, aRest, bRest);

  // --- MAPOWANIE ZMIENNYCH USERA -> x,y dla parsera wielomianĂłw
  const step2Rhs = mapUserVarsToXY(step2RhsRaw, aParsed.var, bParsed.var);
  const step3Rhs = mapUserVarsToXY(step3RhsRaw, aParsed.var, bParsed.var);

  // --- KROK 2 ---
  const userPoly2 = parsePoly(step2Rhs);
  const polyOk2 = !!userPoly2 && polyEq(userPoly2, expected);

  const unexpectedStep2Vars = findUnexpectedVars(step2RhsRaw, [aParsed.var, bParsed.var]);
  const step2VarsOk = unexpectedStep2Vars.length === 0;
  const hasSqParen = step2HasSquaredParentheses(userAnswer.step2 || step2RhsRaw);
  const step2Ok = polyOk2 && !hasSqParen && step2VarsOk;

  // --- KROK 3 ---
  const userPoly3 = parsePoly(step3Rhs);
  const equal3 = !!userPoly3 && polyEq(userPoly3, expected);

  // jawny czynnik m (np. "5(...)" lub "5*(...)")
  const explicitFactor = hasExplicitFactorM(userAnswer.step3 || step3RhsRaw, modulus);

  // sprawdĹş, ĹĽe expected / m ma caĹ‚kowite wspĂłĹ‚czynniki
  const inside = polyDivByInt(expected, modulus);
  const insideOk = !!inside;

  // Sprawdzamy wyłącznie prawą stronę kroku 3 (po ewentualnym "a^2-b^2="),
  // by konsekwentnie pilnować zmiennych z kroku 1.
  const unexpectedStep3Vars = findUnexpectedVars(step3RhsRaw, [aParsed.var, bParsed.var]);
  const step3VarsOk = unexpectedStep3Vars.length === 0;
  const step3Ok = equal3 && explicitFactor && insideOk && step3VarsOk;

  const score = (step1Ok ? 1 : 0) + (step2Ok ? 1 : 0) + (step3Ok ? 1 : 0);

  const hints = [];
  if ((userAnswer.step2 || '').trim() && unexpectedStep2Vars.length > 0) {
    hints.push(
      `Uwaga: Krok 2: użyj konsekwentnie tych samych zmiennych co w kroku 1 (${aParsed.var}, ${bParsed.var}). ` +
        `Wykryto dodatkowe oznaczenia: ${unexpectedStep2Vars.join(', ')}.`
    );
  }

  if ((userAnswer.step2 || '').trim() && !step2Ok && hasSqParen) {
    hints.push(
      'Uwaga: Krok 2: rozwiń kwadraty i zapisz wynik bez postaci typu ( ... )^2 (po uproszczeniu do sumy składników).'
    );
  }

  if ((userAnswer.step3 || '').trim() && unexpectedStep3Vars.length > 0) {
    hints.push(
      `Uwaga: Krok 3: użyj konsekwentnie tych samych zmiennych co w kroku 1 (${aParsed.var}, ${bParsed.var}). ` +
        `Wykryto dodatkowe oznaczenia: ${unexpectedStep3Vars.join(', ')}.`
    );
  }

  return {
    isCorrect: score === 3,
    score,
    maxScore,
    parts: { step1: step1Ok, step2: step2Ok, step3: step3Ok },
    ...(hints.length
      ? {
          messageType: 'warning',
          message:
            hints.length <= 1
              ? hints[0]
              : [
                  hints[0],
                  ...hints.slice(1).map((h) => h.replace(/^Uwaga:\s*/i, '')),
                ].join('\n'),
        }
      : {}),
  };
}

// ============================================================================
// KRĂ“TKA MODEL-ODPOWIEDĹą (trzy kroki) â€“ Ĺ‚adniejszy Krok 3
// ============================================================================

export function renderAnswer({ data }) {
  const { modulus, aRest, bRest, diffQuot } = data;

  const m = modulus;
  const r = aRest;
  const s = bRest;

  const m2 = m * m;
  const twoMr = 2 * m * r;
  const twoMs = 2 * m * s;

  const r2 = r * r;
  const s2 = s * s;

  // (r^2 - s^2)/m â€” u Ciebie jest w data.diffQuot
  const c = typeof diffQuot === "number" ? diffQuot : (r2 - s2) / m;

  // staĹ‚a na koĹ„cu: "", "+5", "-3" (bez "+-3")
  const fmtConstEnd = (k) => (k === 0 ? "" : k > 0 ? `+${k}` : `${k}`);

  // Krok 2: ... + (r^2 - s^2) jako staĹ‚a w peĹ‚nej postaci
  const step2Const = r2 - s2;
  const step2ConstStr = fmtConstEnd(step2Const);

  // Krok 3: ... + c
  const step3ConstStr = fmtConstEnd(c);

  const step1 = `a=${m}x+${r},\\;b=${m}y+${s},\\;x,y\\in\\mathbb{Z}`;

  const step2 = `a^{2}-b^{2}=${m2}x^{2}-${m2}y^{2}+${twoMr}x-${twoMs}y${step2ConstStr}`;

  const step3Inside = `${m}x^{2}-${m}y^{2}+${2 * r}x-${2 * s}y${step3ConstStr}`;
  const step3 = `a^{2}-b^{2}=${m}\\left(${step3Inside}\\right)`;

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1:</strong> <InlineMath math={step1} />
      </p>
      <p>
        <strong>Krok 2:</strong> <InlineMath math={step2} />
      </p>
      <p>
        <strong>Krok 3:</strong> <InlineMath math={step3} />
      </p>
    </div>
  );
}

// ============================================================================
// PEĹNE WYJAĹšNIENIE
// ============================================================================

export function renderExplanation({ data }) {
  const { modulus, aRest, bRest } = data;

  const m = modulus;
  const r = aRest;
  const s = bRest;

  const mStr = String(m);
  const rStr = String(r);
  const sStr = String(s);

  const m2 = m * m;

  const twoMr = 2 * m * r;
  const twoMs = 2 * m * s;

  const r2 = r * r;
  const s2 = s * s;

  const diffSquares = r2 - s2;      // np. 1 - 9 = -8
  const insideConst = diffSquares / m; // całkowite w Twoich parach

  const fmtConstEnd = (k) => (k === 0 ? '' : k > 0 ? `+${k}` : `${k}`);

  // Krok 2: stała już policzona (bez "(1-9)")
  const step2ConstStr = fmtConstEnd(diffSquares);

  // Krok 3: stała na końcu nawiasu bez "+-2"
  const step3ConstStr = fmtConstEnd(insideConst);

  return (
    <div className="space-y-3" style={{ textAlign: 'left', width: '100%', display: 'block' }}>
      <p>
        <strong>Krok 1.</strong> Skoro <InlineMath math={'a'} /> przy dzieleniu przez{' '}
        <InlineMath math={mStr} /> daje resztę <InlineMath math={rStr} />, a{' '}
        <InlineMath math={'b'} /> daje resztę <InlineMath math={sStr} />, to istnieją liczby całkowite{' '}
        <InlineMath math={'x'} />, <InlineMath math={'y'} />, takie że:
      </p>

      <BlockMath
        math={`a = ${mStr}x + ${rStr},\\quad b = ${mStr}y + ${sStr},\\quad x,y \\in \\mathbb{Z}.`}
      />

      <p>
        <strong>Krok 2.</strong> Podstawiamy do <InlineMath math={'a^{2}-b^{2}'} /> i rozwijamy kwadraty:
      </p>

      <BlockMath math={`a^{2}-b^{2} = (${mStr}x+${rStr})^{2} - (${mStr}y+${sStr})^{2}`} />

      <BlockMath
        math={`= \\left(${m2}x^{2} + ${twoMr}x + ${r2}\\right) - \\left(${m2}y^{2} + ${twoMs}y + ${s2}\\right)`}
      />

      <BlockMath
        math={`= ${m2}x^{2} - ${m2}y^{2} + ${twoMr}x - ${twoMs}y ${step2ConstStr}`}
      />

      <p>
        <strong>Krok 3.</strong> Wyłączamy <InlineMath math={mStr} /> przed nawias:
      </p>

      <BlockMath
        math={`a^{2}-b^{2} = ${mStr}\\Big(${mStr}x^{2}-${mStr}y^{2}+${2 * r}x-${2 * s}y${step3ConstStr}\\Big)`}
      />

      <p>
        W nawiasie jest liczba całkowita, więc <InlineMath math={'a^{2}-b^{2}'} /> jest
        wielokrotnością <InlineMath math={mStr} />, czyli jest podzielne przez{' '}
        <InlineMath math={mStr} />. To należało wykazać.
      </p>
    </div>
  );
}

// ============================================================================
// PODGLÄ„D ODPOWIEDZI UCZNIA
// ============================================================================

function renderLine(labelLatex, raw, stripper) {
  if (!raw) return null;
  const rhs = stripper(raw);
  const rhsLatex = toUserLatex(rhs);

  return (
    <p>
      <InlineMath math={labelLatex} /> <InlineMath math={rhsLatex} />
    </p>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    (!userAnswer.step1a && !userAnswer.step1b && !userAnswer.step2 && !userAnswer.step3)
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const rhsA = userAnswer.step1a ? stripForA(userAnswer.step1a) : '';
  const rhsB = userAnswer.step1b ? stripForB(userAnswer.step1b) : '';

  const varA = detectFirstVarLetter(rhsA) || 'x';
  const varB = detectFirstVarLetter(rhsB) || 'y';

  const hintStyle = { marginTop: -6, opacity: 0.85, fontSize: 13 };

  return (
    <div style={{ whiteSpace: 'pre-wrap', marginTop: 8, textAlign: 'left' }}>
      {renderLine('a=', userAnswer.step1a, stripForA)}
      {!!userAnswer.step1a && (
        <p style={hintStyle}>
          <InlineMath math={`${varA} \\in \\mathbb{Z}`} />
        </p>
      )}

      {renderLine('b=', userAnswer.step1b, stripForB)}
      {!!userAnswer.step1b && (
        <p style={hintStyle}>
          <InlineMath math={`${varB} \\in \\mathbb{Z}`} />
        </p>
      )}

      {renderLine('a^{2}-b^{2}=', userAnswer.step2, stripForDiff)}
      {renderLine('a^{2}-b^{2}=', userAnswer.step3, stripForDiff)}
    </div>
  );
}

